import firebase from "firebase/app";
import "firebase/auth";
import { User } from "../types";
import { wrapUser } from "./auth";

export function getPhoneHint(resolver: firebase.auth.MultiFactorResolver): firebase.auth.MultiFactorInfo | null {
  return resolver.hints.find((hint) => hint.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID) ?? null;
}

export async function startMfaSignInChallenge(
  resolver: firebase.auth.MultiFactorResolver,
  recaptchaVerifier: firebase.auth.RecaptchaVerifier
): Promise<string> {
  const hint = getPhoneHint(resolver);
  if (!hint) {
    throw new Error("La cuenta no tiene un segundo factor de teléfono configurado");
  }
  const provider = new firebase.auth.PhoneAuthProvider(firebase.auth());
  return provider.verifyPhoneNumber({ multiFactorHint: hint, session: resolver.session }, recaptchaVerifier);
}

export async function completeMfaSignIn(
  resolver: firebase.auth.MultiFactorResolver,
  verificationId: string,
  code: string
): Promise<User> {
  const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, code);
  const assertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);
  const userCredential = await resolver.resolveSignIn(assertion);
  if (!userCredential.user) {
    throw new Error("No se pudo completar el inicio de sesión");
  }
  return wrapUser(userCredential.user);
}

// ---- Enrollment (self-service, from the user's own account settings) ----

export function getEnrolledPhoneFactor(): firebase.auth.MultiFactorInfo | null {
  const user = firebase.auth().currentUser;
  if (!user) return null;
  return (
    user.multiFactor.enrolledFactors.find((f) => f.factorId === firebase.auth.PhoneMultiFactorGenerator.FACTOR_ID) ??
    null
  );
}

export async function startPhoneEnrollment(
  phoneNumber: string,
  recaptchaVerifier: firebase.auth.RecaptchaVerifier
): Promise<string> {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
  }
  const session = await user.multiFactor.getSession();
  const provider = new firebase.auth.PhoneAuthProvider(firebase.auth());
  return provider.verifyPhoneNumber({ phoneNumber, session }, recaptchaVerifier);
}

export async function completePhoneEnrollment(
  verificationId: string,
  code: string,
  displayName?: string
): Promise<void> {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
  }
  const credential = firebase.auth.PhoneAuthProvider.credential(verificationId, code);
  const assertion = firebase.auth.PhoneMultiFactorGenerator.assertion(credential);
  await user.multiFactor.enroll(assertion, displayName);
}

export async function unenrollPhoneFactor(factor: firebase.auth.MultiFactorInfo): Promise<void> {
  const user = firebase.auth().currentUser;
  if (!user) {
    throw new Error("Se ha perdido la sesión, recargue la página e inicie sesión nuevamente.");
  }
  await user.multiFactor.unenroll(factor);
}
